import React, { useEffect, useRef, useState } from "react";

interface VoiceCommandProps {
    onResult: (text: string) => void;
}

const VoiceCommand = ({ onResult }: VoiceCommandProps) => {
    const [isListening, setIsListening] = useState(false);
    const [supported, setSupported] = useState(true);

    const recognitionRef = useRef<any>(null);
    const shouldContinueRef = useRef(false);

    useEffect(() => {
        const SpeechRecognition =
            (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setSupported(false);
            return;
        }

        const recognition = new SpeechRecognition();

        // Important settings
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-IN";

        recognition.onstart = () => {
            console.log("🎙️ Recording started");
            setIsListening(true);
        };

        recognition.onresult = (event: any) => {
            let transcript = "";

            for (let i = event.resultIndex; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
            }

            console.log("🎤 Transcript:", transcript);

            onResult(transcript);
        };

        recognition.onerror = (event: any) => {
            console.error("🎙️ Speech recognition error:", event.error);

            // These can happen normally when using Chrome
            if (event.error === "no-speech" || event.error === "aborted") {
                return;
            }

            shouldContinueRef.current = false;
            setIsListening(false);
        };

        recognition.onend = () => {
            console.log("🎙️ Recognition ended");

            // Chrome can automatically stop recognition.
            // Restart it while the user still wants to listen.
            if (shouldContinueRef.current) {
                setTimeout(() => {
                    try {
                        recognition.start();

                        console.log("🔄 Recognition restarted");
                    } catch (error) {
                        console.log("Recognition restart skipped");
                    }
                }, 300);
            } else {
                setIsListening(false);
            }
        };

        recognitionRef.current = recognition;

        return () => {
            shouldContinueRef.current = false;

            try {
                recognition.stop();
            } catch (error) {
                console.log("Recognition cleanup skipped");
            }
        };
    }, [onResult]);

    const startListening = () => {
        if (!recognitionRef.current) {
            return;
        }

        shouldContinueRef.current = true;

        try {
            recognitionRef.current.start();

            console.log("▶️ Start listening");
        } catch (error) {
            console.log("Speech recognition already running");
        }
    };

    const stopListening = () => {
        console.log("⏹️ Stop listening");

        shouldContinueRef.current = false;

        try {
            recognitionRef.current?.stop();
        } catch (error) {
            console.log("Recognition stop skipped");
        }

        setIsListening(false);
    };

    if (!supported) {
        return (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-center text-sm text-red-600">
                Voice recognition is not supported in this browser.
                <br />
                Please use Google Chrome.
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={isListening ? stopListening : startListening}
            className={`mt-3 flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 font-semibold transition-all duration-200 ${
                isListening
                    ? "border-red-300 bg-red-50 text-red-600"
                    : "border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
            }`}
        >
            {isListening ? (
                <>
                    <span className="flex h-3 w-3 animate-pulse rounded-full bg-red-500" />
                    Listening... Click to Stop
                </>
            ) : (
                <>🎙️ Start Voice Command</>
            )}
        </button>
    );
};

export default VoiceCommand;
